/**
 * The tail-spring card — ADR-038, the Body tab's spring control: on/off, the
 * MOOD (the drive the spring follows — R5, ruled 2026-09-24), the one MUSCLE
 * knob that scales the mood's base→tip gain profile, the three physics knobs
 * the prior turns into per-joint numbers (stiffness falling toward the tip,
 * drag, gravity), the wag's own knobs, how much of the body's motion the tail
 * inherits, and the reflex flag. The colliders the tail meets are SAID with
 * how they were sized (the status line, on the card's hover — XD 2026-09-29
 * §3B moved every reading off the panel), so a shrunk or dropped collider is
 * never a surprise and never a paragraph.
 *
 * Every control PREVIEWS while it moves and commits ONE `tailSpring.set` on
 * release (the arm-space discipline): the settings are Document data (R3) —
 * undoable, saved, and what the export declares.
 *
 * A pure view over `deps`: it renders what it is handed and reports input.
 * Depends on: panel-registry (types), the tail-spring view type. Depended on by: main.
 */
import type { PanelMount } from './panel-registry.js';
import { withTip } from './tooltip.js';
import type { TailSpringChoice, TailSpringView } from '../viewport/tail-spring.js';
import { TAIL_MOODS, type TailMood } from '@harryhausen/kernel';

export interface TailSpringPanelDeps {
  /**
   * ONE RECEIPT (XD note 2026-09-27 §3.1, PR 4): the status mark for a card's subject, read from the
   * same Body progress the section headers draw — so a card and its header can never disagree.
   * null = the object is not counted (absent).
   */
  readonly mark?: (subject: string) => HTMLElement | null;

  /** Asked at render time — the subject is replaceable. */
  readonly view: () => TailSpringView;
  readonly setOn: (on: boolean) => void;
  /** A live drag (never committed); null clears it. */
  readonly preview: (choice: Partial<TailSpringChoice> | null) => void;
  /** One `tailSpring.set` on release. */
  readonly commit: (choice: Partial<TailSpringChoice>) => void;
  readonly reset: () => void;
  /** Re-render when the subject is remounted (the chain it shows may be new). */
  readonly subjectChanged?: (listener: () => void) => () => void;
  /**
   * THE BODY VOICE (XD 2026-09-29 §3B; Allen: "i tend to lean status"): the card's readings —
   * the per-joint muscle, the flat-tail note, the colliders — go to the status line on hover and
   * focus, not onto the panel. `reveal` for a refusal only.
   */
  readonly say?: (line: string | null, reveal?: boolean) => void;
}

/** Everything true about the spring that is not a control: said, never printed (§3B). */
function readingOf(v: TailSpringView): string {
  const parts = [
    `tail spring: ${v.joints} joints, ${v.source === 'file' ? 'as the file declares' : 'from the tail prior'}`,
    v.passive
      ? 'The file declares a plain spring — no drive: the tail plays physics only against its rest.'
      : `Muscle down the tail (base → tip): ${v.gains.map((g) => g.toFixed(2)).join(' ')}.`,
  ];
  if (v.paddle !== null) parts.push(`Flat tail (${v.paddle.aspect}:1): it bends up and down about its width; sideways bend and twist are held.`);
  parts.push(
    v.colliders.length === 0
      ? 'No colliders — the tail may pass through the legs.'
      : `Collides with ${v.colliders.map((c) => `${c.bone} (${c.kind} ${c.radiusCm} cm, ${c.note})`).join('; ')}.`,
  );
  return parts.join(' · ');
}

const MOOD_TIPS: Record<TailMood, string> = {
  wag: 'happy — a wave driven from the base, carriage raised; the spring carries it to the tip',
  lash: 'irritated — stiff, fast, snapping, in bursts',
  tuck: 'scared — pulled down and in, held hard against the legs',
  still: 'hunting — held low and straight, a tip flick now and then',
  twitch: 'play — the tip alone twitches',
  loose: 'no drive — physics only (and a clip that keys the tail plays under the spring alone)',
};

interface Slider {
  readonly label: string;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly tip: string;
  readonly read: (v: TailSpringView) => number;
  readonly write: (x: number, v: TailSpringView) => Partial<TailSpringChoice>;
  readonly show?: (v: TailSpringView) => boolean;
}

const SLIDERS: readonly Slider[] = [
  { label: 'Muscle', min: 0, max: 1, step: 0.01, tip: 'how tightly the tail follows its mood — 0 is physics only; the base always leads, tendons reach the tip', read: (v) => v.muscle, write: (x) => ({ muscle: x }) },
  { label: 'Stiffness', min: 0, max: 4, step: 0.05, tip: 'how hard the tail returns to its target (at the root; the tip gets 35% of it — the whip)', read: (v) => v.stiffness, write: (x) => ({ stiffness: x }) },
  { label: 'Drag', min: 0, max: 1, step: 0.01, tip: 'how much of its swing the tail loses each step — more drag settles sooner', read: (v) => v.drag, write: (x) => ({ drag: x }) },
  { label: 'Gravity', min: 0, max: 0.5, step: 0.005, tip: 'how hard the tail is pulled down against its stiffness', read: (v) => v.gravity, write: (x) => ({ gravity: x }) },
  { label: 'Inertia', min: 0, max: 1, step: 0.01, tip: 'how much of the body’s motion the tail feels — 0 rides with the body, 1 swings with full world momentum', read: (v) => v.inertia, write: (x) => ({ inertia: x }) },
  { label: 'Wag size', min: 0, max: 120, step: 1, tip: 'the wag’s swing at the base, degrees', read: (v) => v.wag.amplitudeDeg, write: (x, v) => ({ wag: { ...v.wag, amplitudeDeg: x } }), show: (v) => v.mood === 'wag' },
  { label: 'Wag speed', min: 0.1, max: 6, step: 0.05, tip: 'wags per second', read: (v) => v.wag.hz, write: (x, v) => ({ wag: { ...v.wag, hz: x } }), show: (v) => v.mood === 'wag' },
  { label: 'Wag bias', min: -1, max: 1, step: 0.05, tip: 'push the wag to one side (dogs wag right toward what they like, left away)', read: (v) => v.wag.bias, write: (x, v) => ({ wag: { ...v.wag, bias: x } }), show: (v) => v.mood === 'wag' },
];

export function createTailSpringPanel(deps: TailSpringPanelDeps): PanelMount {
  return (container, store) => {
    const card = document.createElement('section');
    card.className = 'hand-card tail-spring-card';
    card.dataset['testid'] = 'tail-spring';
    container.append(card);
    // SHORTHAND FIRST (Body-tab XD note 2026-09-27 §3.3): the first view is on · the mood chips
    // (the presets) · Muscle (the one Amount); settle, the physics and the reflex sit behind
    // Fine-tune — the expert's second visit, hidden outright in Creator (Allen, 2026-09-29) — and
    // every reading goes to the status line. Its open state survives a re-render.
    let fineOpen = false;
    // A refusal reaches the eye ONCE per sentence: the card shows it short, the status line
    // carries the whole of it, revealed — and a re-render does not re-shout it over a newer line.
    let refusalSaid: string | null = null;
    const say = (): void => {
      const v = deps.view();
      if (v.available) deps.say?.(readingOf(v));
      else if (v.refusal !== null) deps.say?.(v.refusal, true);
    };
    card.addEventListener('pointerenter', say);
    card.addEventListener('focusin', say);

    const render = (): void => {
      const v = deps.view();
      card.replaceChildren();
      const title = document.createElement('h3');
      title.textContent = 'Tail spring';
      const springMark = deps.mark?.('Tail spring') ?? null;
      if (springMark !== null) title.append(springMark);
      card.append(title);
      if (!v.available) {
        // KEEP the refusal, short (fail-loud: it must reach the eye); the reason rides the status
        // line. "Grow a tail…" is DELETED — the card only mounts once a chain exists (main.ts).
        if (v.refusal !== null) {
          const p = document.createElement('p');
          p.className = 'hand-refusal';
          p.textContent = 'Tail spring refused.';
          card.append(p);
          if (refusalSaid !== v.refusal) {
            refusalSaid = v.refusal;
            deps.say?.(v.refusal, true);
          }
        }
        return;
      }
      refusalSaid = null;
      const onRow = document.createElement('label');
      onRow.className = 'tail-spring-on';
      const on = document.createElement('input');
      on.type = 'checkbox';
      on.checked = v.on;
      on.setAttribute('aria-label', 'tail spring on');
      on.addEventListener('change', () => deps.setOn(on.checked));
      onRow.append(on, document.createTextNode(' on'));
      // The export line was a paragraph under Fine-tune; it is HELP, so it rides the on-box's tip
      // (§3B). Its "Off —" twin is DELETED: an empty box already says the export declares nothing.
      card.append(
        ...withTip(
          onRow,
          `${v.joints} joints, ${v.source === 'file' ? 'as the file declares' : 'from the tail prior'} — exports as VRMC_springBone 1.0 plus the drive (mood, muscle, limits) in the spring’s extras — World layers it once its reader lands (ADR-038).`,
        ),
      );
      if (v.passive) {
        // The betrayal test keeps this one: with a plain file spring the moods do NOTHING, and a
        // person pressing them must be told why without hovering. A pill, not a sentence.
        const pill = document.createElement('span');
        pill.className = 'status-pill';
        pill.dataset['state'] = 'fine';
        pill.textContent = 'physics only';
        onRow.append(' ', pill);
      }

      // The mood: the drive the spring follows (or that plays alone with the spring off).
      const moods = document.createElement('div');
      moods.className = 'tail-spring-moods';
      moods.setAttribute('role', 'radiogroup');
      moods.setAttribute('aria-label', 'tail mood');
      for (const mood of TAIL_MOODS) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = mood === v.mood ? 'hand-revert active' : 'hand-revert';
        b.dataset['mood'] = mood;
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-checked', String(mood === v.mood));
        b.textContent = mood;
        b.addEventListener('click', () => deps.commit({ mood }));
        moods.append(...withTip(b, MOOD_TIPS[mood]));
      }
      card.append(moods);

      const fine = document.createElement('details');
      fine.className = 'tail-spring-finetune';
      fine.open = fineOpen;
      fine.addEventListener('toggle', () => {
        fineOpen = fine.open;
      });
      const fineSummary = document.createElement('summary');
      fineSummary.textContent = 'Fine-tune';
      fine.append(fineSummary);
      // SETTLE, ONCE, at the top of Fine-tune (§3C): in Creator, off-and-on again already settles.
      const reset = document.createElement('button');
      reset.type = 'button';
      reset.className = 'hand-revert';
      reset.textContent = 'settle';
      reset.addEventListener('click', () => deps.reset());
      fine.append(...withTip(reset, 'put the tail back on its target and let it go again'));

      for (const k of SLIDERS) {
        if (k.show !== undefined && !k.show(v)) continue;
        const row = document.createElement('div');
        row.className = 'value-row tail-spring-knob';
        const label = document.createElement('span');
        label.className = 'vr-label';
        label.textContent = k.label;
        const slider = document.createElement('input');
        slider.type = 'range';
        slider.min = String(k.min);
        slider.max = String(k.max);
        slider.step = String(k.step);
        slider.value = String(k.read(v));
        slider.setAttribute('aria-label', `tail spring ${k.label.toLowerCase()}`);
        const value = document.createElement('span');
        value.className = 'vr-field';
        const fmt = (x: number): string => x.toFixed(k.step < 0.01 ? 3 : k.step >= 1 ? 0 : 2);
        value.textContent = fmt(k.read(v));
        slider.addEventListener('input', () => {
          value.textContent = fmt(Number(slider.value));
          deps.preview(k.write(Number(slider.value), v));
        });
        slider.addEventListener('change', () => deps.commit(k.write(Number(slider.value), v)));
        row.append(label, ...withTip(slider, k.tip), value);
        // Muscle is the one Amount; every other knob is the expert's.
        (k.label === 'Muscle' ? card : fine).append(row);
      }

      const reflexRow = document.createElement('label');
      reflexRow.className = 'tail-spring-on';
      const reflex = document.createElement('input');
      reflex.type = 'checkbox';
      reflex.checked = v.reflex;
      reflex.setAttribute('aria-label', 'tail reflex');
      reflex.addEventListener('change', () => deps.commit({ reflex: reflex.checked }));
      reflexRow.append(
        ...withTip(reflex, 'balance: the tail swings against the body’s turn (a flip, a spin) — measured, off until it reads right'),
        document.createTextNode(' reflex'),
      );
      fine.append(reflexRow);
      // The gains, the flat-tail note and the colliders were three paragraphs here; they are
      // READINGS, so they go to the status line on the card's hover (readingOf, §3B).
      card.append(fine);

    };
    const unsubscribe = store.subscribe(render);
    const unsubscribeSubject = deps.subjectChanged?.(render) ?? (() => undefined);
    render();
    return {
      dispose() {
        unsubscribe();
        unsubscribeSubject();
        card.remove();
      },
    };
  };
}
